import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, X } from "lucide-react";
import type { Product } from "@/components/DesignResults";

interface SaveAfterViewPromptProps {
  product: Product | null;
  onSave: () => void;
  onDismiss: () => void;
}

const SaveAfterViewPrompt = ({ product, onSave, onDismiss }: SaveAfterViewPromptProps) => {
  // Auto-dismiss after 8 seconds
  useEffect(() => {
    if (!product) return;
    const t = window.setTimeout(onDismiss, 8000);
    return () => window.clearTimeout(t);
  }, [product, onDismiss]);

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          className="fixed z-50 left-3 right-3 bottom-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm"
          role="dialog"
          aria-label="Save this piece"
        >
          <div className="bg-card border border-border shadow-elegant rounded-xl p-3 sm:p-4 flex gap-3 items-start">
            {product.thumbnail && (
              <img
                src={product.thumbnail}
                alt=""
                className="w-14 h-14 sm:w-16 sm:h-16 object-cover bg-muted shrink-0 rounded-md"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground leading-snug">
                Save this piece to come back to later?
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                {product.title}
              </p>
              <div className="flex items-center gap-2 mt-2.5">
                <button
                  type="button"
                  onClick={onSave}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold tracking-normal bg-primary-strong text-primary-strong-foreground rounded-full hover:opacity-90 transition-opacity"
                >
                  <Heart className="w-3.5 h-3.5" />
                  Save
                </button>
                <button
                  type="button"
                  onClick={onDismiss}
                  className="px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Not now
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss"
              className="shrink-0 p-1 -m-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SaveAfterViewPrompt;
